/**
 * Onyx — Trust & Safety Dashboard
 * Full violations queue, real-time config sync, actions panel, audit log.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Shield, RefreshCw, Play, AlertTriangle, Ban, MessageCircleWarning,
  VolumeX, Clock, CheckCircle, XCircle, Eye, Search, Filter,
  Activity, BarChart3, FileText, Settings, ChevronDown, ListPlus, Trash2
} from 'lucide-react';

const SEV_COLORS = { real_money: '#0d9488', spam: '#f59e0b', harassment: '#ef4444', hate_speech: '#dc2626', nudity: '#ec4899', violence: '#b91c1c', cheating: '#7c3aed', illegal: '#991b1b', profanity: '#6b7280', scam: '#ea580c', impersonation: '#0891b2' };
const ACTION_STYLES = { warn: { bg: 'bg-amber-100', text: 'text-amber-800', icon: MessageCircleWarning }, mute: { bg: 'bg-orange-100', text: 'text-orange-800', icon: VolumeX }, suspend: { bg: 'bg-purple-100', text: 'text-purple-800', icon: Clock }, ban: { bg: 'bg-red-100', text: 'text-red-800', icon: Ban }, dismiss: { bg: 'bg-gray-100', text: 'text-gray-600', icon: XCircle } };
const STATUS_STYLES = { pending_review: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pending' }, auto_actioned: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auto-Actioned' }, resolved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Resolved' }, dismissed: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Dismissed' } };
const SOURCE_LABELS = { stream_chat: 'Stream Chat', game_chat: 'Game Chat', post: 'Post', comment: 'Comment', profile: 'Profile', user_report: 'User Report', message: 'Direct Message' };   // Round 39: DMs

const OnyxDashboard = () => {
  const { api } = useAdmin();
  const [tab, setTab] = useState('queue');
  const [violations, setViolations] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [config, setConfig] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState('');
  const [newTermCat, setNewTermCat] = useState('profanity');
  const [savingTerms, setSavingTerms] = useState(false);

  // Round 35: the three calls are independent — one slow or failing call can
  // no longer hold the whole page on the spinner; each has its own 15 s limit
  // and whatever did load is shown, with a Retry for the rest.
  const fetchAll = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterStatus) params.append('status', filterStatus);
    if (filterCat) params.append('category', filterCat);
    const opts = { timeout: 15000 };
    const [vRes, mRes, cRes] = await Promise.allSettled([
      api.get(`/api/admin/workforce/onyx/violations?${params}`, opts),
      api.get('/api/admin/workforce/onyx/scan-metrics', opts),
      api.get('/api/admin/workforce/onyx/config', opts),
    ]);
    const failed = [];
    if (vRes.status === 'fulfilled') {
      setViolations(vRes.value.data.violations || []);
      setPendingCount(vRes.value.data.pending_count || 0);
    } else failed.push('violations');
    if (mRes.status === 'fulfilled') setMetrics(mRes.value.data); else failed.push('metrics');
    if (cRes.status === 'fulfilled') {
      setConfig(cRes.value.data);
      setTerms(cRes.value.data?.config?.custom_terms || []);
    } else failed.push('settings');
    setLoadError(failed.length ? `Could not load: ${failed.join(', ')}` : '');
    if (failed.length) toast.error(`Onyx: could not load ${failed.join(', ')}`);
    setLoading(false);
  }, [api, filterStatus, filterCat]);

  const saveTerms = async (next) => {
    setSavingTerms(true);
    try {
      const res = await api.post('/api/admin/workforce/onyx/config', { custom_terms: next });
      setTerms(res.data?.config?.custom_terms || []);
      setConfig((c) => (c ? { ...c, config: res.data?.config || c.config } : c));
      toast.success('Word list saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the word list'); }
    finally { setSavingTerms(false); }
  };

  const addTerm = () => {
    const t = newTerm.trim().toLowerCase().replace(/\s+/g, ' ');
    if (t.length < 2 || t.length > 60) return toast.error('A word or phrase, 2–60 characters');
    if (terms.some((x) => x.term === t)) return toast.error('Already on the list');
    setNewTerm('');
    saveTerms([...terms, { term: t, category: newTermCat }]);
  };

  const fetchAudit = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/workforce/onyx/audit-log');
      setAuditLogs(res.data.logs || []);
    } catch {}
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab, fetchAudit]);

  const runScan = async () => {
    setActionLoading('scan');
    try {
      const res = await api.post('/api/admin/workforce/onyx/scan');
      toast.success(`Scan complete: ${res.data.total_violations} violations found`);
      fetchAll();
    } catch { toast.error('Scan failed'); }
    finally { setActionLoading(''); }
  };

  const takeAction = async (violationId, action, notes = '') => {
    setActionLoading(action);
    try {
      await api.post('/api/admin/workforce/onyx/action', { violation_id: violationId, action, admin_notes: notes });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} applied`);
      setSelected(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-gray-500" /></div>;
  const categoryOptions = Object.entries(config?.violation_categories || {});
  const sensitivity = config?.config?.sensitivity_level || 'medium';
  const humanReview = config?.config?.human_review_required;
  const monitoredTypes = config?.config?.monitored_violations || [];

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full" data-testid="onyx-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Onyx — Trust & Safety</h1>
            <p className="text-sm text-gray-500">Real-time platform monitoring & enforcement</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={runScan} disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            data-testid="onyx-scan-btn">
            <Play className={`w-4 h-4 ${actionLoading === 'scan' ? 'animate-pulse' : ''}`} />
            {actionLoading === 'scan' ? 'Scanning...' : 'Run Full Scan'}
          </button>
          <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {loadError && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="onyx-load-error">
          <span>{loadError}. Everything else on this page is current.</span>
          <button onClick={fetchAll} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700">Retry</button>
        </div>
      )}

      {/* Config Status Bar */}
      <div className="bg-white border rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-gray-500 text-xs">Sensitivity</span>
            <p className={`font-bold ${sensitivity === 'high' ? 'text-red-600' : sensitivity === 'low' ? 'text-green-600' : 'text-amber-600'}`} data-testid="onyx-sensitivity">
              {sensitivity.toUpperCase()}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Monitored Types</span>
            <p className="font-bold text-gray-800">{monitoredTypes.length} active</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Auto-Warn</span>
            <p className={`font-bold ${config?.config?.auto_warn_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
              {config?.config?.auto_warn_enabled ? 'ON' : 'OFF'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Auto-Suspend</span>
            <p className={`font-bold ${config?.config?.auto_suspend_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
              {config?.config?.auto_suspend_enabled ? 'ON' : 'OFF'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Human Review</span>
            <p className={`font-bold ${humanReview ? 'text-blue-600' : 'text-gray-400'}`} data-testid="onyx-human-review">
              {humanReview ? 'REQUIRED' : 'OFF'}
            </p>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mt-2">Config source: Admin &gt; Moderation &gt; Bot Settings</p>
      </div>

      {/* Metrics Row */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-2">
          {[
            { l: 'Total', v: metrics.total_violations, c: 'text-gray-700' },
            { l: 'Pending', v: metrics.pending_review, c: metrics.pending_review > 0 ? 'text-amber-600' : 'text-gray-600' },
            { l: 'Resolved', v: metrics.resolved, c: 'text-emerald-600' },
            { l: '24h', v: metrics.violations_24h, c: 'text-blue-600' },
            { l: '7d', v: metrics.violations_7d, c: 'text-violet-600' },
            { l: 'Actions', v: metrics.actions_taken, c: 'text-gray-700' },
            { l: 'Actions 24h', v: metrics.actions_24h, c: 'text-cyan-600' },
            { l: 'Safety tips 24h', v: metrics.safety_nudges_24h ?? 0, c: 'text-emerald-600' },   // Round 39: private DM heads-ups
            { l: 'Scans', v: metrics.total_scans, c: 'text-gray-600' },
          ].map(m => (
            <div key={m.l} className="bg-white border rounded-lg p-3 text-center">
              <p className={`text-lg font-bold ${m.c}`}>{m.v}</p>
              <p className="text-[10px] text-gray-500">{m.l}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { k: 'queue', l: `Violations Queue${pendingCount > 0 ? ` (${pendingCount})` : ''}`, icon: AlertTriangle },
          { k: 'breakdown', l: 'Breakdown', icon: BarChart3 },
          { k: 'words', l: `Word List${terms.length ? ` (${terms.length})` : ''}`, icon: ListPlus },
          { k: 'audit', l: 'Audit Log', icon: FileText },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.k ? 'border-gray-800 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`onyx-tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {/* QUEUE TAB */}
      {tab === 'queue' && (
        <div className="space-y-3">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="text-xs border rounded-lg px-2.5 py-1.5 bg-white" data-testid="filter-status">
              <option value="">All Statuses</option>
              <option value="pending_review">Pending Review</option>
              <option value="auto_actioned">Auto-Actioned</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="text-xs border rounded-lg px-2.5 py-1.5 bg-white" data-testid="filter-category">
              <option value="">All Categories</option>
              {Object.entries(config?.violation_categories || {}).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Violations List */}
            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Violations ({violations.length})
              </div>
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {violations.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 text-sm">No violations found. Run a scan to detect content.</div>
                ) : violations.map(v => {
                  const s = STATUS_STYLES[v.status] || STATUS_STYLES.pending_review;
                  return (
                    <div key={v.id}
                      onClick={() => setSelected(v)}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition ${selected?.id === v.id ? 'bg-gray-100 border-l-4 border-gray-800' : ''}`}
                      data-testid={`violation-${v.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <img src={v.user_picture || `https://ui-avatars.com/api/?name=${v.user_handle}&size=24&background=374151&color=fff`} alt="" className="w-5 h-5 rounded-full" />
                          <span className="text-xs font-medium text-gray-800">@{v.user_handle}</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span>
                      </div>
                      <p className="text-sm text-gray-700 truncate">{v.content_snapshot}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                        <span className="px-1.5 py-0.5 rounded text-white font-medium" style={{ background: SEV_COLORS[v.primary_category] || '#6b7280' }}>{v.primary_category}</span>
                        <span>{SOURCE_LABELS[v.source_type] || v.source_type}</span>
                        <span>conf: {(v.confidence * 100).toFixed(0)}%</span>
                        <span className="ml-auto">{v.created_at ? new Date(v.created_at).toLocaleTimeString() : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Detail + Actions */}
            <div className="bg-white border rounded-xl overflow-hidden">
              {!selected ? (
                <div className="p-12 text-center text-gray-400">
                  <Eye className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Select a violation to review</p>
                </div>
              ) : (
                <div className="flex flex-col h-full max-h-[560px]">
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={selected.user_picture || `https://ui-avatars.com/api/?name=${selected.user_handle}&size=32&background=374151&color=fff`} alt="" className="w-8 h-8 rounded-full" />
                        <div>
                          <p className="font-semibold text-gray-900">@{selected.user_handle}</p>
                          <p className="text-[10px] text-gray-500">{selected.user_name}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${(STATUS_STYLES[selected.status] || STATUS_STYLES.pending_review).bg} ${(STATUS_STYLES[selected.status] || STATUS_STYLES.pending_review).text}`}>
                        {(STATUS_STYLES[selected.status] || STATUS_STYLES.pending_review).label}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    <div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">Content Evidence</span>
                      <div className="mt-1 bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-gray-800">{selected.content_snapshot}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Category</span>
                        <p className="font-medium" style={{ color: SEV_COLORS[selected.primary_category] || '#6b7280' }}>
                          {(config?.violation_categories?.[selected.primary_category]?.label) || selected.primary_category}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Confidence</span>
                        <p className="font-medium">{(selected.confidence * 100).toFixed(0)}%</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Source</span>
                        <p className="font-medium">{SOURCE_LABELS[selected.source_type] || selected.source_type}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Detected</span>
                        <p className="font-medium">{selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Patterns Matched</span>
                        <p className="font-mono text-xs">{(selected.matched_patterns || []).join(', ')}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Recommended</span>
                        <p className="font-bold text-red-600 uppercase">{selected.recommended_action}</p>
                      </div>
                    </div>
                    {selected.action_taken && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <span className="text-[10px] font-bold text-emerald-600 uppercase">Action Taken</span>
                        <p className="text-sm font-medium text-emerald-800">{selected.action_taken} — by {selected.resolved_by || 'system'}</p>
                        {selected.admin_notes && <p className="text-xs text-emerald-700 mt-1">{selected.admin_notes}</p>}
                      </div>
                    )}
                  </div>

                  {selected.status === 'pending_review' && (
                    <div className="border-t p-4 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Take Action</p>
                      <div className="flex flex-wrap gap-2">
                        {['warn', 'mute', 'suspend', 'ban', 'dismiss'].map(action => {
                          const s = ACTION_STYLES[action];
                          const Icon = s.icon;
                          return (
                            <button key={action}
                              onClick={() => takeAction(selected.id, action)}
                              disabled={!!actionLoading}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${s.bg} ${s.text} hover:opacity-80 disabled:opacity-50`}
                              data-testid={`action-${action}`}>
                              <Icon className="w-3.5 h-3.5" /> {action.charAt(0).toUpperCase() + action.slice(1)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BREAKDOWN TAB */}
      {tab === 'breakdown' && metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Category</h3>
            <div className="space-y-2">
              {Object.entries(metrics.category_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                <div key={cat} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: SEV_COLORS[cat] || '#6b7280' }} />
                    <span className="text-gray-700 capitalize">{cat.replace('_', ' ')}</span>
                  </div>
                  <span className="font-bold">{count}</span>
                </div>
              ))}
              {Object.keys(metrics.category_breakdown || {}).length === 0 && <p className="text-gray-400 text-sm">No data yet</p>}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Source</h3>
            <div className="space-y-2">
              {Object.entries(metrics.source_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
                <div key={src} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{SOURCE_LABELS[src] || src}</span>
                  <span className="font-bold">{count}</span>
                </div>
              ))}
              {Object.keys(metrics.source_breakdown || {}).length === 0 && <p className="text-gray-400 text-sm">No data yet</p>}
            </div>
          </div>
        </div>
      )}

      {/* WORD LIST TAB (Round 35) */}
      {tab === 'words' && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="onyx-word-list">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <p className="text-sm font-semibold text-gray-700">Your word list</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Words or phrases Onyx should flag on top of its built-in detection (slurs, harassment, sexual solicitation, threats,
              scams and off-platform payment steering, doxxing, real-money side deals). Whole words only, any capitalisation.
              The category decides what happens: Profanity warns, Harassment mutes, the rest go to review.
            </p>
          </div>
          <div className="p-4 flex flex-wrap gap-2 border-b">
            <input value={newTerm} onChange={(e) => setNewTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addTerm(); }}
              placeholder="word or phrase" maxLength={60} data-testid="onyx-word-input"
              className="flex-1 min-w-[180px] px-3 py-2 border rounded-lg text-sm" />
            <select value={newTermCat} onChange={(e) => setNewTermCat(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="onyx-word-category">
              {categoryOptions.map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
            </select>
            <button onClick={addTerm} disabled={savingTerms || !newTerm.trim()}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50" data-testid="onyx-word-add">
              Add
            </button>
          </div>
          <div className="divide-y max-h-[420px] overflow-y-auto">
            {terms.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">Nothing added yet — the built-in detection is already running.</div>
            ) : terms.map((t) => (
              <div key={t.term} className="px-4 py-2.5 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{t.term}</span>
                  <span className="px-1.5 py-0.5 rounded text-white text-[10px] font-medium" style={{ background: SEV_COLORS[t.category] || '#6b7280' }}>
                    {config?.violation_categories?.[t.category]?.label || t.category}
                  </span>
                </div>
                <button onClick={() => saveTerms(terms.filter((x) => x.term !== t.term))} disabled={savingTerms}
                  className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50" aria-label={`Remove ${t.term}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AUDIT LOG TAB */}
      {tab === 'audit' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b text-sm font-semibold text-gray-700">Enforcement Audit Trail</div>
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {auditLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">No enforcement actions yet</div>
            ) : auditLogs.map(l => (
              <div key={l.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${(ACTION_STYLES[l.action] || ACTION_STYLES.warn).bg} ${(ACTION_STYLES[l.action] || ACTION_STYLES.warn).text}`}>
                      {l.action?.toUpperCase()}
                    </span>
                    <span className="font-medium text-gray-900">@{l.user_handle}</span>
                  </div>
                  <span className="text-xs text-gray-400">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{l.reason}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">by {l.executed_by} {l.automated ? '(automated)' : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnyxDashboard;
